<template>
  <div>
    <div class="d-flex justify-space-between ga-3 mb-3">
      <b-input
        v-if="keepArray(filterMap).some(f => f.key === 'search')"
        v-model="filterData.search"
        :disabled="$loading.isLoading(`server-table__${id}`)"
        :loading="$loading.isLoading(`server-table__${id}`)"
        :placeholder="searchPlaceholder"
        class="w-100"
        hide-details
        prepend-inner-icon="mdi-magnify"
        @update:model-value="search"></b-input>
      <div class="d-flex ga-3">
        <v-btn
          v-if="keepArray(filterMap).filter(f => f.key !== 'search').length"
          border
          color="white"
          @click="toggleIsOpen">
          <v-icon class="mr-2" icon="mdi-filter-variant"></v-icon>
          <span class="font-weight-medium">Фільтри</span>
        </v-btn>
        <slot name="action-button"></slot>
      </div>
    </div>

    <div v-if="keepArray(filterMap).filter(f => f.key !== 'search').length">
      <v-card v-show="isOpen" border class="mb-3">
        <div class="d-flex">
          <v-card-text
            class="bg-grey-lighten-5"
            style="max-width: 300px; border-right: thin solid #ddd">
            <div class="font-weight-bold mb-3">Параметри фільтрації</div>

            <b-form hide-action-buttons @submit="addFilter">
              <b-select
                v-model="filterForm.key"
                :error-messages="getErrorMessages(v$.key)"
                :item-title="f => keyToLabel(f.key.replace('[]', ''), scope)"
                :items="keepArray(filterMap).filter(f => f.key !== 'search')"
                class="mb-3"
                hide-details
                item-value="key"
                label="Поле"></b-select>

              <b-select
                v-if="!filterForm.key"
                :model-value="null"
                class="mb-3"
                disabled
                hide-details
                label="Значення"></b-select>

              <filter-input
                v-else
                v-model="filterForm.value"
                :filter-map-item="
                  keepArray(filterMap).find(f => f.key === filterForm.key)
                "
                :v$="v$.value"
                class="mb-3">
              </filter-input>

              <v-btn block type="submit"> Додати параметр </v-btn>
            </b-form>
          </v-card-text>
          <v-card-text>
            <div class="d-flex justify-space-between ga-3 mb-3 align-center">
              <span class="font-weight-bold">Активні параметри</span>

              <v-fade-transition hide-on-leave>
                <v-btn
                  v-show="hasSelectedFilters"
                  density="comfortable"
                  variant="outlined"
                  @click="clearFilters()">
                  Очистити все
                </v-btn>
              </v-fade-transition>
            </div>
            <div v-if="hasSelectedFilters" class="d-flex ga-3 flex-wrap">
              <active-filter-item
                v-for="filter in visibleActiveFilters"
                :key="filter[0]"
                :filter-map-item="
                  keepArray(filterMap).find(f => {
                    return (
                      f.key === filter[0] ||
                      f.dateRangeKeys?.includes(filter[0])
                    )
                  })
                "
                :title="keyToLabel(filter[0].replace('[]', ''), scope)"
                :value="filter[1]"
                @click:close="updateFilter(filter[0], $event)">
              </active-filter-item>
            </div>
            <div v-else class="text-disabled">
              Додайте параметр фільтрації для відбору списку
            </div>
          </v-card-text>
        </div>
      </v-card>

      <div v-show="!isOpen && hasSelectedFilters" class="mb-3">
        <div class="d-flex ga-3 flex-wrap">
          <active-filter-item
            v-for="filter in visibleActiveFilters"
            :key="filter[0]"
            :filter-map-item="
              keepArray(filterMap).find(f => {
                return (
                  f.key === filter[0] || f.dateRangeKeys?.includes(filter[0])
                )
              })
            "
            :title="keyToLabel(filter[0].replace('[]', ''), scope)"
            :value="filter[1]"
            @click:close="updateFilter(filter[0], $event)">
          </active-filter-item>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, inject, PropType, reactive, ref, watch } from 'vue'
import { serverTableIdKey } from 'best-modules/components/server-table/injectionKeys'
import { useRoute } from 'vue-router'
import { getErrorMessages, isEmptyValue, keepArray } from 'best-modules/utils'
import { debounce, omit } from 'lodash'
import { dictionary } from '@/utils/key-to-label/dictionary'
import { keyToLabel } from '@/utils/key-to-label'
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { BForm } from 'best-modules/components/'
import ActiveFilterItem from './ActiveFilterItem.vue'
import { FilterMapItem } from './types'
import FilterInput from './FilterInput.vue'
import { fillDirectory } from '@/directory'

export default {
  name: 'ServerFilter',
  components: { BForm, ActiveFilterItem, FilterInput },
  props: {
    filterMap: {
      type: Array as PropType<FilterMapItem[]>,
    },
    searchPlaceholder: {
      type: String,
      default: 'Пошук...',
    },
    scope: {
      type: String as PropType<keyof typeof dictionary>,
      default: 'base',
    },
    hiddenActiveFilterKeys: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
  },
  methods: { getErrorMessages, omit, keyToLabel, keepArray },
  emits: ['filter'],
  setup(props, { emit }) {
    const id = inject(serverTableIdKey)
    const route = useRoute()
    const isOpen = ref<boolean>(false)
    const filterData = reactive<Record<string, any>>({
      ...omit(route.query, 'page'),
    })

    for (const f of keepArray(props.filterMap)) {
      if (route.query[f.key] && f.directory) {
        fillDirectory(f.directory)
      }
    }

    const filterForm = reactive({
      key:
        keepArray(props.filterMap).filter(f => f.key !== 'search')[0]?.key ||
        null,
      value: null,
    })

    const rules = {
      key: { required },
      value: { required },
    }
    const v$ = useVuelidate(rules, filterForm)

    watch(
      () => filterForm.key,
      () => {
        filterForm.value = null
        v$.value.value.$reset()
      }
    )

    const updateFilter = (key: any, value: any) => {
      keepArray(key).forEach((k, idx) => {
        filterData[k] = keepArray(value)[idx] || null
      })
      emit('filter', filterData)
    }

    const search = debounce((searchStr: string) => {
      updateFilter('search', searchStr)
    }, 500)

    const addFilter = () => {
      v$.value.$validate()
      if (!v$.value.$invalid) {
        const filterMapItem = keepArray(props.filterMap).find(
          f => f.key === filterForm.key
        )
        console.log('filterMapItem', filterMapItem)
        console.log('filterForm', filterForm)
        if (filterMapItem.dateRangeKeys) {
          return updateFilter(filterMapItem.dateRangeKeys, filterForm.value)
        }
        if (filterMapItem.component === 'autocomplete-multiple') {
          return updateFilter(filterMapItem.key, [filterForm.value])
        }
        updateFilter(filterMapItem.key, filterForm.value)
      }
    }

    const hasSelectedFilters = computed(() => {
      return Object.entries(filterData)
        .filter(([key]) => !props.hiddenActiveFilterKeys.includes(key))
        .map(([, value]) => value)
        .reduce((acc, f) => {
          return !!acc || !isEmptyValue(f)
        }, null)
    })
    const visibleActiveFilters = computed(() => {
      return Object.entries(omit(filterData, 'search')).filter(
        ([key, value]) => {
          return value && !props.hiddenActiveFilterKeys.includes(key)
        }
      )
    })

    const clearFilters = (keys: any) => {
      if (hasSelectedFilters.value) {
        if (keys?.length) {
          updateFilter(keys, null)
        } else {
          updateFilter(
            Object.keys(filterData).filter(
              key => !props.hiddenActiveFilterKeys.includes(key)
            ),
            null
          )
        }
      }
    }
    const toggleIsOpen = () => {
      isOpen.value = !isOpen.value
    }

    return {
      filterData,
      isOpen,
      hasSelectedFilters,
      clearFilters,
      toggleIsOpen,
      updateFilter,
      id,
      filterForm,
      v$,
      addFilter,
      search,
      visibleActiveFilters,
    }
  },
}
</script>

<style lang="scss" scoped></style>
